import { Dialog } from "@headlessui/react";
import { useEffect, useRef, useState } from "react";
import Button from "../../../components/button/Button";
import { Label, TextField } from "../../../components/fields/Fields";
import { Modal } from "../../../components/modal/Modal";
import { ModalProp } from "../../../components/modal/Modal";
import { EndDateType, EveryMonthType, Messages, RepeatIntervalType, messageSchema, newMessage } from "../../../model/Message";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import FormController from "../../../components/fields/FormController";
import { FormBox, ModalFoterBox } from "../../../components/box/Box";
import { SelectFields } from "../../../components/select/SelectFields";
import { ReplyButton } from "../../../model/ReplyButton";
import { OnlineUrls } from "../../../model/OnlineUrls";
import { CheckBoxGroup, CheckBoxItem } from "../../../components/checkBox/CheckBoxGroup";
import { useInput } from "../../../hooks/useInput";
import { Company } from "../../../model/Companies";
import InputFile from "../../../components/input/InputFile";
import { PhotoIcon } from "@heroicons/react/24/solid";
import { useLocation } from "react-router-dom";

export type MessageProps = {
  message?: Messages;
  company?: Company;
  onClickPreview: (data: Messages) => Promise<void>;
  onDelivered: (data: Messages) => Promise<void>;
  onReplyButtonSelect: (id: string) => void;
  replyButtonList?: ReplyButton[];
  setReplyButtonList: React.Dispatch<React.SetStateAction<ReplyButton[]>>
  defaultReplyButtonList?: ReplyButton[];
  eventReplyButtonList?: ReplyButton[];
  replyButtonSelected?: ReplyButton | undefined;
  onOnlineUrlSelect: (id: string) => void;
  onEventTypeSelect: (eventType: string) => void;
  selectedEventType?: string;
  onSendImage: (file: File) => void;
  setBase64Image: React.Dispatch<React.SetStateAction<string>>;
  base64Image: string;
  onlineUrlList?: OnlineUrls[];
  onlineUrlSelected?: string;
};

export const MessageEditModal = ({
  open, toggle, closeOnClickOutside,
  message = newMessage(),
  company,
  onClickPreview, onDelivered,
  onReplyButtonSelect,
  replyButtonList,
  setReplyButtonList,
  defaultReplyButtonList = [],
  eventReplyButtonList = [],
  replyButtonSelected,
  onOnlineUrlSelect, onlineUrlList, onlineUrlSelected,
  onEventTypeSelect, selectedEventType = '',
  onSendImage, setBase64Image, base64Image = '',
}: ModalProp & MessageProps) => {
  const location = useLocation();

  const [repeatIntervalTypeSelected, setRepeatIntervalTypeSelected] = useState<RepeatIntervalType>('weekly');
  const [everyMonthItemSelected, setEveryMonthItemSelected] = useState<EveryMonthType>('day');
  const [endDateTypeSelected, setEndDateTypeSelected] = useState<EndDateType>('none');

  const { control, trigger, handleSubmit, watch, getValues, setValue, reset } =
    useForm<Messages>({
      resolver: yupResolver(messageSchema),
      defaultValues: message,
    });

  // submit押下フラグ
  const submitProcessing = useRef(false);

  useEffect(() => {
    if(message.draftFlg || message.sendDate > new Date()){
      // 入力項目に値をセット
      setValue('id', message.id);
      setValue('body', message.body);
      setValue('urlButtonFlg', message.urlButtonFlg ?? false);
      setValue('urlButtonName', message.urlButtonName ?? '');
      setValue('urlLink', message.urlLink ?? '');
      setValue('title', message.title);
      setValue('topic', message.topic);
      setValue('contactInformation', message.contactInformation);
      setValue('eventVenue', message.eventVenue);
      setValue('image', message.image);
      setValue('type', message.type);
      setValue('link', message.link);
      setValue('fileupload', message.fileupload);
      setValue('draftFlg', message.draftFlg);
      setValue('authorUid', message.authorUid);
      setValue('deliveredFlg', message.deliveredFlg);
      setValue('eventFlg', message.eventFlg);
      setValue('eventType', message.eventType);
      setValue('regularFlg', message.regularFlg);
      setValue('regularSetting', message.regularSetting);
      setValue('url', message.url ?? 'https://');

      // 時間の設定関係
      setValue('createAt', message.createAt);
      setValue('sendDate', message.sendDate);
      if(message.draftFlg || message.sendDate > new Date()){
        setValue('sendDateTime', `${('00' + message.sendDate.getHours()).slice(-2)}:${('00' + message.sendDate.getMinutes()).slice(-2)}`);
      };
      setValue('eventAt', message.eventAt);
      setValue('eventEndAt', message.eventEndAt);
      if(!message.isEventTimeEmpty) {
        setValue('eventAtTime', `${('00' + message.eventAt.getHours()).slice(-2)}:${('00' + message.eventAt.getMinutes()).slice(-2)}`);
        setValue('eventEndAtTime', `${('00' + message.eventEndAt.getHours()).slice(-2)}:${('00' + message.eventEndAt.getMinutes()).slice(-2)}`);
      };
      if(message.publishEndAt !== undefined){
        setValue('publishEndAt', message.publishEndAt);
        setValue('publishEndAtTime', `${('00' + message.publishEndAt?.getHours()).slice(-2)}:${('00' + message.publishEndAt?.getMinutes()).slice(-2)}`);
      };
      // 繰り返し配信の項目(再編集の時には未使用というかそもそも保存してない？)
      setValue('regularSetting.repeatIntervalType', message.regularSetting?.repeatIntervalType ?? 'weekly');
      setValue('regularSetting.everyMonthType', message.regularSetting?.everyMonthType);
      setValue('regularSetting.dayOfWeek', message.regularSetting?.dayOfWeek?.map((item: any): boolean => item.value));
      setValue('regularSetting.endDateType', message.regularSetting?.endDateType ?? 'none');
      const tempDate = message.regularSetting?.endDate ?? new Date();
      setValue('regularSetting.endDate',new Date(tempDate.getFullYear(),tempDate.getMonth(),tempDate.getDate()));
      setValue('regularSetting.numberOfRepeats', message.regularSetting?.numberOfRepeats);

      // 返信ボタンの初期データの選択 通常タイプとイベントタイプ
      const replyButtonList = message.eventFlg ? eventReplyButtonList : defaultReplyButtonList;
      setReplyButtonList(replyButtonList);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message.authorUid, message.body, message.createAt, message.deliveredFlg,
    message.draftFlg, message.eventAt, message.eventEndAt, message.eventType,
    message.fileupload, message.id, message.image, message.link, message.contactInformation,
    message.eventVenue, message.sendDate, message.title, message.topic,
    message.type, message.url, message.publishEndAt, setValue]);

  useEffect(() => {
    // 新規の場合
    if(!message.draftFlg && message.sendDate <= new Date() && open) {
      // 時間の設定関係
      onOnlineUrlSelect('none');
      setValue('createAt', new Date());
      setValue('sendDate', new Date());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[message.draftFlg, open, setValue]);

  useEffect(() => {
    // 予定表掲載チェックボックスの値で返信ボタンの内容を入れ替え
    const replyButtonList = getValues("eventFlg") ? eventReplyButtonList : defaultReplyButtonList;
    setReplyButtonList(replyButtonList);
    if(replyButtonList.length > 0 ){
      onReplyButtonSelect('undefined');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[watch("eventFlg")]);

  useEffect(() => {
    // イベントタイプのセレクトボックスの変更を監視して値を入れ替え
    setValue('eventType', selectedEventType);
  },[selectedEventType, setValue, onEventTypeSelect]);

  useEffect(() => {
    // 返信ボタンのセレクトボックスの変更を監視して値を入れ替え
    setValue('replyButton', replyButtonSelected?.name ?? '');
  },[replyButtonSelected, setValue, onReplyButtonSelect]);

  useEffect(() => {
    // ミーティングURLのセレクトボックスの変更を監視して値を入れ替え
    setValue('link', onlineUrlSelected ?? '');
    if(onlineUrlSelected === '' && (message.url === undefined || message.url === '')){
      setValue('url', 'https://');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[onlineUrlSelected, setValue, onOnlineUrlSelect]);

  useEffect(() => {
    // 登録後のフォームを初期化
    submitProcessing.current = false; 
    if(!open && !(message.draftFlg || message.sendDate > new Date())) {
      reset();
      // 配信時間のリセット
      setValue('sendDateTime', '');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[open, reset, setValue]);

  // イベントタイプ作成
  const eventTypeList = company?.eventType?.map((item) => {
    return {
      id: item,
      name: item,
      value: item
    }
  }) ?? [];
  eventTypeList.unshift({id: 'undefined', name: '', value: ''});

  // 時分選択用の項目作成
  const timeSelectList = [];
  for(let i = 0; i < 2400; i += 15) {
    const time = ('0000' + i).slice(-4);
    timeSelectList.push(`${time.substring(0,2)}:${time.substring(2)}`);
    if(time.lastIndexOf('45', 4) > 0) i += 40;
  };

  // 配信日 日付け選択後に時分を設定
  const onChangeDate = (e: any) => {
    const dateTime = new Date(e.target.value);
    const name = e.target.name;
    const timeName: any = `${name}Time`;
    const nowHoursString = (new Date().getHours()).toString().padStart(2, '0');
    const nowMinutsString = (new Date().getMinutes()).toString().padStart(2, '0');
    const value = getValues(timeName) === '' || getValues(timeName) === '--:--'
      ? `${nowHoursString}:${nowMinutsString}`
      : getValues(timeName) ?? '00:00';
    dateTime.setHours(Number(value.substring(0, 2)));
    dateTime.setMinutes(Number(value.substring(3)));
    dateTime.setSeconds(Number(0));
    setValue(name, dateTime);
    if (name === "sendDate") {
      const dateTimeDate = new Date(
        dateTime.getFullYear(),
        dateTime.getMonth(),
        dateTime.getDate()
      )
      const nowDate = new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        new Date().getDate()
      )
      if (dateTimeDate.getTime() === nowDate.getTime() && (getValues(timeName) === undefined || getValues(timeName) === '' || getValues(timeName) === '--:--')) {
        // 配信日が処理日かつ配信時間が空欄の場合
        dateTime.setHours(Number(nowHoursString));
        dateTime.setMinutes(Number(nowMinutsString));
        dateTime.setSeconds(Number(0));
        setValue(name, dateTime);
      }
    } else if (name === "publishEndAt" && Number.isNaN((new Date(dateTime)).getTime())) {
      // 掲載終了日が空欄の場合
      setValue(name, undefined);
    }
    if(submitProcessing.current === true){
      // 配信日入力チェック
      trigger("sendDate");
      // 配信時間入力チェック
      trigger("sendDateTime");
      // 掲載終了日入力チェック
      trigger("publishEndAt");
      // イベント開催日入力チェック
      trigger("eventAt");
      // 開始時間入力チェック
      trigger("eventAtTime");
    }
  };

  // イベント開催日 日付け選択後に時分を設定
  const onEventChangeDate = (e: any) => {
    const dateTime = new Date(e.target.value);
    const name = e.target.name;

    const nowHoursString = (new Date().getHours()).toString().padStart(2, '0');
    const nowMinutsString = (new Date().getMinutes()).toString().padStart(2, '0');

    // 開始時間の設定
    const timeName: any = `${name}Time`;
    const value = getValues(timeName) === '' || getValues(timeName) === '--:--'
      ? `${nowHoursString}:${nowMinutsString}`
      : getValues(timeName) ?? '00:00';
    dateTime.setHours(Number(value.substring(0, 2)));
    dateTime.setMinutes(Number(value.substring(3)));
    dateTime.setSeconds(Number(0));
    setValue(name, dateTime);

    // 終了時間の設定
    const endTimeName: any = `eventEndAtTime`;
    const endValue = getValues(endTimeName) === '' || getValues(endTimeName) === '--:--'
      ? `${nowHoursString}:${nowMinutsString}`
      : getValues(endTimeName) ?? '00:00';
    dateTime.setHours(Number(endValue.substring(0, 2)));
    dateTime.setMinutes(Number(endValue.substring(3)));
    dateTime.setSeconds(Number(0));
    setValue('eventEndAt', dateTime);

    if(submitProcessing.current === true){
      // イベント開催日入力チェック
      trigger(name);
      // 開始時間入力チェック
      trigger("eventAtTime");
      // 掲載終了日入力チェック
      trigger("publishEndAt");
    }
  };

  // 開始時間、終了時間 時分選択後に日時フィールドに反映
  const onChangeTime = (e: any) => {
    const id = e.target.name.toString().replace('Time', '');
    const idDate = new Date(getValues(id));
    if(e.target.value !== null && typeof(e.target.value) === 'string'){
      const value = e.target.value;
      idDate.setHours(Number(value.substring(0, 2)));
      idDate.setMinutes(Number(value.substring(3)));
      idDate.setSeconds(Number(0));
      if(id === 'sendDate' && value === ''){
        idDate.setHours(new Date().getHours());
        idDate.setMinutes(new Date().getMinutes());
        idDate.setSeconds(new Date().getSeconds());
      };
      setValue(id, idDate);
      setValue(e.target.name, e.target.value);
    };
    if(submitProcessing.current === true){
      // 開始時間、終了時間入力チェック
      trigger(e.target.name);
      // 開始時間変更時、終了時間入力チェック
      if(e.target.name === "eventAtTime"){
      	trigger("eventEndAtTime");
      }
    }
  };

  const onChangeFlg = (e: any) => {
    setValue(e.target.name, !getValues(e.target.name));
  };

  // 繰り返し配信設定用項目
  const repeatIntervalType: {id:RepeatIntervalType, name:string}[] = [
    { id: 'weekly', name: '週間ごと' },
    { id: 'monthly', name: 'か月ごと' },
  ];
  const endDateType: {id: EndDateType, name: string}[] = [
    { id: 'none', name: 'なし' },
    { id: 'endDate', name: '終了日' },
    { id: 'numberOfRepeats', name: '回数' },
  ];
  const [selectedSendDate, setSelectedSendDate] = useState(new Date());
  useEffect(() => {
    setSelectedSendDate(new Date(getValues('sendDate')));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[watch('sendDate'), getValues]);
  const dayOfWeekItem: CheckBoxItem[] = [
    { id: 'sun', title: '日', value: selectedSendDate.getDay() === 0 },
    { id: 'mon', title: '月', value: selectedSendDate.getDay() === 1 },
    { id: 'tue', title: '火', value: selectedSendDate.getDay() === 2 },
    { id: 'wed', title: '水', value: selectedSendDate.getDay() === 3 },
    { id: 'thu', title: '木', value: selectedSendDate.getDay() === 4 },
    { id: 'fri', title: '金', value: selectedSendDate.getDay() === 5 },
    { id: 'sat', title: '土', value: selectedSendDate.getDay() === 6 }
  ];
  const [dayOfWeekState, setDayOfWeekState] = useState(dayOfWeekItem);
  const dayOfWeek = useInput(dayOfWeekState);
  const getDayAndCount = (date: Date) => {
    return {day: date.getDay(), count: Math.floor((date.getDate() - 1) / 7) + 1};
  };
  const weekName = dayOfWeekItem.map((item) => item.title);
  const dayCount = getDayAndCount(selectedSendDate);
  const everyMonthItem: {id: EveryMonthType, name: string}[] = [
    { id: 'day', name: `毎月${selectedSendDate.getDate()}日` },
    { id: 'week', name: `毎月第${dayCount.count} ${weekName[dayCount.day]}曜日` }
  ];
  useEffect(() => {
    if(getValues('regularFlg')){
      setValue('regularSetting.repeatIntervalType', repeatIntervalTypeSelected);
      setValue('regularSetting.everyMonthType', everyMonthItemSelected);
      setValue('regularSetting.dayOfWeek', dayOfWeekState.map((item: any): boolean => item.value));
      setValue('regularSetting.endDateType', endDateTypeSelected);
      setValue('regularSetting.endDate',
        new Date(
          selectedSendDate.getFullYear(),
          selectedSendDate.getMonth(),
          selectedSendDate.getDate()
        )
      );
      setValue('regularSetting.numberOfRepeats', 1);
      setValue('regularSetting.previousDeliveryDate', 1);
    }
  },[
    getValues,
    setValue,
    repeatIntervalTypeSelected,
    everyMonthItemSelected,
    endDateTypeSelected,
    dayOfWeekState,
    selectedSendDate,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    watch('regularFlg'),
  ]);
  useEffect(() => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[watch("urlButtonFlg")]);

  return (
    <Modal
      open={open}
      toggle={toggle}
      closeOnClickOutside={closeOnClickOutside}
    >
      <div className="overflow-x-auto">
        <Dialog.Panel className="transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-[calc(100vw_-_25rem)] min-w-[1024px] max-w-[1280px]">
          <form
            onSubmit={handleSubmit(onClickPreview)}
            noValidate
          >
            <div className="p-4">
              <h3 className="text-lg font-medium leading-6 text-gray-800">
                {!getValues("eventFlg") ? 'お知らせ' : '予定'}{getValues('draftFlg') ? '編集' : '作成'}
              </h3>
            </div>
            <div className="overflow-y-auto px-8 text-left pb-8 h-fit max-h-[calc(100vh_-_11rem)] sm:max-h-[calc(100vh_-_10rem)]">
              <FormBox gridcol={"grid-cols-2"} className={'grid !pt-[0px] !gap-8'}>
                <div className="flex flex-col gap-5">
                  <FormController
                    control={control}
                    name={"title"}
                    label={"タイトル"}
                    type={"text"}
                    className="h-[4.5rem]"
                    required
                  />
                  {getValues("eventFlg") && (
                    <>
                      <div className="grid grid-cols-3 items-start gap-4">
                        <FormController
                          control={control}
                          name={"eventAt"}
                          label={"イベント開催日"}
                          type={"date"}
                          onChange={onEventChangeDate}
                          required
                        />
                        <FormController
                          control={control}
                          name={"eventAtTime"}
                          label={"開始時間"}
                          type={"time"}
                          step={900}
                          min={'00:00'}
                          max={'23:45'}
                          list={'eventAtItem'}
                          onChange={onChangeTime}
                          required
                        />
                        <datalist id={'eventAtItem'}>
                          {timeSelectList.map((time, index) => {
                            return (<option key={`eventAtItem${time}-${index}`} value={time}></option>)
                          })}
                        </datalist>
                        <FormController
                          control={control}
                          name={"eventEndAtTime"}
                          label={"終了時間"}
                          type={"time"}
                          step={900}
                          min={'00:00'}
                          max={'23:45'}
                          list={'eventEndAtItem'}
                          onChange={onChangeTime}
                          required
                        />
                        <datalist id={'eventEndAtItem'}>
                          {timeSelectList.map((time, index) => {
                            return (<option key={`eventEndAtItem${time}-${index}`} value={time}></option>)
                          })}
                        </datalist>
                      </div>
                      <FormController
                        control={control}
                        name={"eventVenue"}
                        label={"会場"}
                        type={"text"}
                      />
                    </>
                  )}
                  <Controller
                    name="body"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        type="textarea"
                        label="本文"
                        className={`h-[22rem] pb-11`}
                        error={fieldState.error}
                        helperText={fieldState.error?.message}
                        required
                      />
                    )}
                  />
                  <div className={"flex flex-col gap-1"}>
                    <Label id="" className="self-start block text-sm font-medium text-gray-700">画像添付</Label>
                    <div className={`flex items-center justify-center${base64Image === '' ? " gap-1 flex-col" : " gap-2"}`}>
                      {base64Image === ''
                        ? (<>
                          <InputFile
                            id={'image'}
                            size={"sm"}
                            color={"indigo"}
                            className={"items-center min-w-[132px] h-8"}
                            children={(<>
                              画像を選択
                            </>)}
                            onChange={(e) => {
                              const files = e.target.files;
                              if(files && files[0]){
                                onSendImage(files[0]);
                              }
                            }}
                          />
                          <span className="text-sm font-medium text-gray-500 items-center justify-center">
                            画像は<span className="text-red-500">10MB</span>以下、1ファイルのみです。
                            また、画像形式は<span className="text-red-500">JPEG・PNG</span>のみです。
                          </span>
                        </>)
                        : (<>
                          <img alt={'添付画像'} src={base64Image} className={"max-h-[16rem] max-w-[16rem]"}/>
                          <Button
                            onClick={() => setBase64Image('')}
                            outline={true}
                            children={'キャンセル'}
                            size={"sm"}
                            className={"w-[132px] h-8"}
                          />
                        </>)}
                    </div>
                  </div>

                  <FormBox
                    gridcol="grid-cols-1"
                    className={'p-4 border border-gray-300 rounded-md shadow-sm bg-yellow-50'}>
                    <div className={'flex items-center'}>
                      <Label id={"urlButtonFlg"} className={'mr-4'}>
                        URLボタンあり
                      </Label>
                      <Controller
                        name={"urlButtonFlg"}
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            type="checkbox"
                            className={'h-6'}
                            onChange={onChangeFlg}
                            error={fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </div>
                    {getValues("urlButtonFlg") && (<>
                      <FormController
                        control={control}
                        name={"urlButtonName"}
                        label={"URLボタン名"}
                        type={"text"}
                        className={"w-1/2"}
                        required={getValues("urlButtonFlg")}
                      />
                      <FormController
                        control={control}
                        name={"urlLink"}
                        label={"URL"}
                        type={"text"}
                        required={getValues("urlButtonFlg")}
                      />
                    </>)}
                  </FormBox>
                  <FormController
                    control={control}
                    name={"contactInformation"}
                    label={"問い合わせ先"}
                    type={"text"}
                    className="h-auto"
                  />
                </div>
                <div className="flex flex-col gap-5">
                  <Controller
                    name={"replyButton"}
                    control={control}
                    render={({ field, fieldState }) => (
                    <SelectFields
                      {...field}
                      label="返信ボタン"
                      color="indigo"
                      className="h-[4.5rem]"
                      selectClassName="px-3 py-2 h-auto"
                      onChange={(e: any) => {onReplyButtonSelect(e.target.value);field.onChange(e)}}
                      value={replyButtonSelected?.id}
                      selectItems={replyButtonList?.map((item) => {
                        return {
                          id: item.id,
                          name: item.id !== "undefined"
                            ? `${item.name}: ${item.buttonName.join(" / ")}`
                            : item.name
                        }
                      })}
                      error={fieldState.error}
                      helperText={fieldState.error?.message}
                      required={getValues('eventFlg')}
                    />
                    )}
                  />
                  {getValues("eventFlg") && (
                    <>
                      <Controller
                        name={"link"}
                        control={control}
                        render={({ field, fieldState }) => (
                          <SelectFields
                            {...field}
                            id="link"
                            label="ミーティングURL"
                            color="indigo"
                            onChange={(e: any) => {onOnlineUrlSelect(e.target.value);}}
                            value={onlineUrlSelected}
                            className={""}
                            selectItems={onlineUrlList?.map((item) => {
                              return {
                                id: item.id,
                                name: item.id !== "none" && item.id !== 'input'
                                  ? `${item.name}: ${item.url}`
                                  : item.name,
                                value: item.url
                              }
                            })}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                      {(onlineUrlSelected === '')
                        ? (<FormController
                          control={control}
                          name={"url"}
                          label={""}
                          type={"url"}
                          className={'h-12'}
                        />)
                        : (<></>)
                      }
                      <Controller
                        name={"eventType"}
                        control={control}
                        render={({ field, fieldState }) => (
                          <SelectFields
                            {...field}
                            label="イベントタイプ"
                            color="indigo"
                            onChange={(e: any) => {onEventTypeSelect(e.target.value);field.onChange(e)}}
                            value={selectedEventType}
                            className={"h-16"}
                            selectItems={eventTypeList}
                            error={fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </>
                  )}
                  <div className="grid grid-cols-3 items-start gap-4">
                    <FormController
                      control={control}
                      name={"sendDate"}
                      label={"配信日"}
                      type={"date"}
                      onChange={onChangeDate}
                      errMsgWhitespaceWrap
                      required
                    />
                    <FormController
                      control={control}
                      name={"sendDateTime"}
                      label={"配信時間"}
                      type={"time"}
                      step={900}
                      min={'00:00'}
                      max={'23:45'}
                      list={'sendDateItem'}
                      onChange={onChangeTime}
                      errMsgWhitespaceWrap
                    />
                    <datalist id={'sendDateItem'}>
                      {timeSelectList.map((time, index) => {
                        return (<option key={`sendDateItem${time}-${index}`} value={time}></option>)
                      })}
                    </datalist>
                    <FormController
                      control={control}
                      name={"publishEndAt"}
                      label={"掲載終了日"}
                      onChange={onChangeDate}
                      type={"date"}
                      errMsgWhitespaceWrap
                    />
                  </div>
                  {/* 繰り返し配信
                  <FormBox
                    gridcol="sm:grid-cols-1"
                    className={'sm:pt-4 sm:p-4 border border-gray-300 rounded-md shadow-sm bg-indigo-50'}
                  >
                    <div className={'flex items-center'}>
                      <Label id={"regularFlg"} className={'mr-4'}>繰り返し配信する</Label>
                      <TextField
                        name={"regularFlg"}
                        type="checkbox"
                        className={'h-6'}
                        onChange={onChangeFlg}
                        required
                      />
                    </div>
                    {getValues("regularFlg") && (<>
                      <FormBox gridcol={'sm:grid-cols-1'} className={'sm:pt-[0px]'}>
                        <FormBox className={'sm:pt-[0px]'}>
                          <FormController
                            control={control}
                            name={"regularSetting.repeatIntervalNum"}
                            label={"繰り返す間隔"}
                            type={"number"}
                          />
                          <FormBox gridcol={'sm:grid-cols-1'} className={'flex salf-end'}>
                            <Controller
                              name={"regularSetting.repeatIntervalType"}
                              control={control}
                              render={({ field, fieldState }) => (
                              <SelectFields
                                {...field}
                                id="repeatIntervalType"
                                color="indigo"
                                className={'h-10'}
                                value={repeatIntervalTypeSelected}
                                onChange={(e: any) => {
                                  setRepeatIntervalTypeSelected(e.target.value);
                                }}
                                selectItems={repeatIntervalType?.map((item) => {
                                  return {
                                    id: item.id,
                                    name: item.name
                                  }
                                })}
                                error={fieldState.error}
                                helperText={fieldState.error?.message}
                              />)}
                            />
                          </FormBox>
                        </FormBox>
                        {repeatIntervalTypeSelected === 'monthly'
                          && (<>
                            <SelectFields
                              label=''
                              id="everyMonthType"
                              color="indigo"
                              name={'regularSetting.everyMonthType'}
                              value={everyMonthItemSelected}
                              onChange={(e: any) => {
                                setEveryMonthItemSelected(e.target.value);
                              }}
                              selectItems={everyMonthItem?.map((item) => {
                                return {
                                  id: item.id,
                                  name: item.name
                                }
                              })}
                              className={'h-16'}
                            />
                          </>)}
                        {repeatIntervalTypeSelected === 'weekly'
                          && (<>
                            <CheckBoxGroup
                              children={undefined}
                              checkBoxItem={dayOfWeekState}
                              name={"regularSetting.dayOfWeek"}
                              value={dayOfWeek}
                              vertical
                              isTiny
                              setState={setDayOfWeekState}
                            />
                          </>)}
                        <FormBox className={'sm:pt-[0px]'}>
                          <SelectFields
                            label='配信終了日'
                            id="endDateType"
                            color="indigo"
                            name={'regularSetting.endDateType'}
                            value={endDateTypeSelected}
                            onChange={(e: any) => {
                              setEndDateTypeSelected(e.target.value);
                            }}
                            selectItems={endDateType?.map((item) => {
                              return {
                                id: item.id,
                                name: item.name
                              }
                            })}
                          />
                          {endDateTypeSelected === 'endDate'
                            && (<>
                              <FormController
                                control={control}
                                name={"regularSetting.endDate"}
                                label={"終了日"}
                                type={"date"}
                              />
                            </>)}
                          {endDateTypeSelected === 'numberOfRepeats'
                            && (<>
                            <FormController
                              control={control}
                              name={"regularSetting.numberOfRepeats"}
                              label={"回数"}
                              type={"number"}
                            />
                            </>)}
                        </FormBox>
                        {getValues("eventFlg") && (<>
                          <FormBox gridcol={'sm:grid-cols-1'} className={'sm:pt-4 sm:p-4 border border-gray-300 rounded-md shadow-sm bg-violet-200'}>
                            <FormController
                              control={control}
                              name={"regularSetting.previousDeliveryDate"}
                              label={"イベントの何日前に配信するか設定"}
                              type={"number"}
                              className={"h-16"}
                            />
                          </FormBox>
                        </> )}
                      </FormBox>
                    </>)}
                  </FormBox> */}
                </div>
              </FormBox>
            </div>
            <ModalFoterBox>
              <Button
                className="mx-2 mt-1 w-[132px]"
                outline
                onClick={toggle}
                children={'キャンセル'}
              />
              <Button
                className="mt-1 mr-2 w-[132px]"
                onClick={() => onDelivered(getValues())}
                color="violet"
                children={'下書き保存'}
              />
              <Button
                className="mt-1 w-[132px]"
                submit
                onClick={() => submitProcessing.current = true}
                children={'確認'}
              />
            </ModalFoterBox>
          </form>
        </Dialog.Panel>
      </div>
    </Modal>
  );
};
